import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { describeTerms } from '@affily/shared/commission';
import { formatCount, formatCents } from '@affily/shared/money';
import { programPhase } from '@affily/shared/programDates';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { TransparencyBadge, SelfReported } from '../../components/Transparency.jsx';
import { Button, Field, Textarea, Select, Alert, Spinner, EmptyState } from '../../components/ui.jsx';

/** Invite one creator to one of the brand's programs. */
export function BrandInvite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const creator = useFetch(`/creators/${slug}`);
  const programs = useFetch('/programs/mine/list');

  const [programId, setProgramId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // An invitation is fine outside an application window, but not once the program has ended.
  const active = (programs.data?.programs ?? []).filter(
    (p) => p.status === 'active' && programPhase(p) !== 'ended',
  );
  const c = creator.data?.creator;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.post('/invitations', { programId, creatorId: c.id, message });
      navigate('/dashboard/brand/applications');
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  if (creator.loading || programs.loading) {
    return (
      <DashboardLayout side="brand">
        <Spinner />
      </DashboardLayout>
    );
  }

  if (creator.error) {
    return (
      <DashboardLayout side="brand" title="Invite a creator">
        <EmptyState title={creator.error.message} action={<Button to="/creators">Browse creators</Button>} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout side="brand" title={`Invite ${c.displayName}`}>
      <div className="max-w-2xl space-y-6">
        <div className="rounded-lg border border-rule bg-paper-raised p-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <Link to={`/creators/${c.slug}`} className="font-medium hover:text-accent">
                {c.displayName}
              </Link>
              {c.headline && <p className="text-sm text-ink-muted">{c.headline}</p>}
              <p className="mt-1.5 flex flex-wrap items-center gap-2 text-sm">
                <span className="tabular-nums">{formatCount(c.totalFollowers)} followers</span>
                <SelfReported />
              </p>
            </div>
            <TransparencyBadge transparency={c.transparency} size="sm" showDetail={false} />
          </div>
          {c.startingRates.length > 0 && (
            <p className="mt-3 border-t border-rule pt-3 text-sm text-ink-muted">
              Published rates from{' '}
              <span className="font-medium text-ink">
                {formatCents(Math.min(...c.startingRates.map((r) => r.amountCents)))}
              </span>
            </p>
          )}
        </div>

        {active.length === 0 ? (
          <EmptyState
            title="No active programs to invite them to"
            action={<Button to="/dashboard/brand/programs/new">Create a program</Button>}
          >
            Only active programs can take invitations — their terms have to be public first.
          </EmptyState>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Alert>{error}</Alert>

            <Field label="Program" required>
              <Select value={programId} onChange={(e) => setProgramId(e.target.value)} required>
                <option value="">Choose a program…</option>
                {active.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title} — {describeTerms(p)}
                  </option>
                ))}
              </Select>
            </Field>

            <Field
              label="Message"
              constraint={rule.max(2000)}
              hint="They can already read the full terms. Say why you think they specifically are a fit."
            >
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={2000}
              />
            </Field>

            <div className="border-t border-rule pt-5">
              <Button type="submit" disabled={busy || !programId}>
                {busy ? 'Sending…' : 'Send invitation'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}

/** A brand-side entry point into the public creator directory. */
export function BrandFindCreators() {
  return (
    <DashboardLayout
      side="brand"
      title="Find creators"
      description="The same directory everyone else sees — rates public, stats labelled by provenance."
    >
      <div className="rounded-lg border border-rule bg-paper-raised p-6">
        <p className="text-ink-muted">
          Browse and filter by niche, platform, audience size, country and budget. Open a profile
          and use “Invite to a program”.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button to="/creators">Open the creator directory</Button>
          <Button to="/dashboard/brand/applications" variant="outline">
            See pending invitations
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
